import React, { useEffect, useState } from "react";
import { ModalProps } from "@components/atoms/Modal/types";
import SlideSheet from "@components/atoms/SlideSheet";
import WebPopup from "@components/atoms/PopUpWeb";
import Toast, { ToastConfig } from "react-native-toast-message";
import { toastConfig } from "@components/molecules/VDBToast";
import { useAppSettings } from "@redux/slices/globalSlice";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getEmbeddedPopupMaxHeight } from "@utils/iframeMessaging";
import { StyleSheet, useWindowDimensions, View } from "react-native";

const Modal = ({
  type,
  headerTitle,
  children,
  isOpen,
  onClose,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  minModalHeight,
  backgroundColor,
  headerBackgroundColor,
  headerTextColor,
  enableToastMessage = false,
  skipKeyboardAware,
  fillContentHeight,
  size,
  widthPercent,
  style,
  hideHeader,
  transparent,
  noPadding,
  webHeight = "auto",
  showScrollbar = true,
  allowFullScreen,
  isJBT = false,
  disableScrollWeb = false,
  skipKeyboardAwareWeb = false,
  height = "auto",
  clearChat = false,
  handleClearChat = () => {},
  leftIcon,
  leftIconPress,
}: ModalProps) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { height: windowHeight } = useWindowDimensions();
  const requestedType = type ? type : theme.isLargeScreen ? "popUp" : "sheet";
  // Inside an iframe a bottom sheet sits at the bottom of the frame, which is
  // often off the visible page. Use the centered popup instead.
  const isSheetAsPopup = requestedType === "sheet" && !!userInfo.isIFrame();
  const modalType = isSheetAsPopup ? "popUp" : requestedType;
  // `vh` matches the iframe. A tall page must not grow the dialog to the
  // device, and a short results page must not let it spill out of the frame.
  const embeddedMaxHeight = getEmbeddedPopupMaxHeight(windowHeight);
  const requestedHeightFillsFrame =
    typeof height === "string" &&
    (height.endsWith("%") || height.endsWith("vh"));
  const requestedHeightExceedsScreen =
    typeof webHeight === "number" && webHeight > embeddedMaxHeight;
  const popupHeight =
    embeddedMaxHeight &&
    (requestedHeightFillsFrame || requestedHeightExceedsScreen)
      ? `${embeddedMaxHeight}px`
      : webHeight;
  const popupMaxHeight = embeddedMaxHeight
    ? `${embeddedMaxHeight}px`
    : undefined;
  const popupSkipKeyboardAware = isSheetAsPopup
    ? skipKeyboardAwareWeb || !!skipKeyboardAware
    : skipKeyboardAwareWeb;
  const getAppSettings = useAppSettings();
  const [visibilityTime, setVisibilityTime] = useState();
  useEffect(() => {
    if (getAppSettings.toast_display_time) {
      setVisibilityTime(getAppSettings.toast_display_time);
    }
  }, [getAppSettings]);
  return (
    <>
      {modalType === "sheet" ? (
        <SlideSheet
          onClose={onClose as () => void}
          isOpen={isOpen}
          headerTitle={headerTitle}
          paddingLeft={paddingLeft}
          paddingRight={paddingRight}
          paddingTop={paddingTop}
          paddingBottom={paddingBottom}
          minModalHeight={minModalHeight}
          backgroundColor={backgroundColor}
          style={style}
          skipKeyboardAware={skipKeyboardAware}
          fillContentHeight={fillContentHeight}
          allowFullScreen={allowFullScreen}
          height={height}
          clearChat={clearChat}
          handleClearChat={handleClearChat}
          leftIcon={leftIcon}
          leftIconPress={leftIconPress}
        >
          {children}
          {enableToastMessage && (
            <View style={styles.toastLayer} pointerEvents="box-none">
              <Toast
                config={toastConfig as unknown as ToastConfig}
                visibilityTime={visibilityTime ? Number(visibilityTime) : 5000}
              />
            </View>
          )}
        </SlideSheet>
      ) : (
        <WebPopup
          onClose={onClose as () => void}
          isOpen={isOpen}
          title={headerTitle}
          size={size}
          widthPercent={widthPercent}
          backgroundColor={backgroundColor}
          headerBackgroundColor={headerBackgroundColor}
          headerTextColor={headerTextColor}
          hideHeader={hideHeader}
          transparent={transparent}
          noPadding={noPadding}
          height={
            typeof popupHeight === "number" ? `${popupHeight}` : popupHeight
          }
          maxHeight={popupMaxHeight}
          showScrollbar={showScrollbar}
          isJBT={isJBT}
          disableScrollWeb={disableScrollWeb}
          style={style}
          skipKeyboardAwareWeb={popupSkipKeyboardAware}
          leftIcon={leftIcon}
          leftIconPress={leftIconPress}
        >
          {children}
          {enableToastMessage && (
            <View style={styles.toastLayer} pointerEvents="box-none">
              <Toast
                config={toastConfig as unknown as ToastConfig}
                visibilityTime={visibilityTime ? Number(visibilityTime) : 5000}
              />
            </View>
          )}
        </WebPopup>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  toastLayer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    elevation: 9999,
  },
});

export default Modal;
